"use client";

import { useState } from "react";
import styles from "./BrandTypography.module.css";
import CMSLink from "../common/CMSLink";

function DownloadIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 13 13" fill="none">
      <path d="M6.5 1v8m0 0L4 6.5m2.5 2.5L9 6.5M1.5 12h10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

const WEIGHT_VALUE = {
  Light:     300,
  Regular:   400,
  Medium:    500,
  SemiBold:  600,
  Bold:      700,
  ExtraBold: 800,
};

// next/font exposes the loaded families as CSS variables; map CMS font names
// onto them so the preview renders in the real typeface.
function fontStack(name) {
  const key = (name || "").toLowerCase();
  if (key.includes("bricolage")) return "var(--font-brg), sans-serif";
  if (key === "inter") return "inherit";
  return `'${name}', sans-serif`;
}

// CMS may send the alphabet as one string; show each run (A–Z, a–z, 0–9) on its own line.
function alphabetLines(text) {
  return text.split(/\s+/).filter(Boolean);
}

function getValidColor(colorObj) {
  if (typeof colorObj === "string") return colorObj;
  if (colorObj?.metaColor) {
    const { r, g, b, a } = colorObj.metaColor;
    return `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, ${a})`;
  }
  return undefined;
}

function TypefaceCard({ tf }) {
  const weights = tf.weights || [];
  const [active, setActive] = useState(
    weights.some((w) => w.label === "Regular") ? "Regular" : weights[0]?.label,
  );

  const color = getValidColor(tf.text_color) || "#ffffff";
  const font = fontStack(tf.font_name);
  const dlConfig = {
    // A plain string URL is an external link (e.g. a Google Fonts page).
    url:
      typeof tf.download_url === "string"
        ? { type: "external", link: tf.download_url }
        : tf.download_url,
    filename: tf.download_filename || `${tf.font_name || "font"}.zip`,
    force_download: Boolean(tf.force_download),
  };

  return (
    <div className={styles.card} style={{ color, fontFamily: font }}>
      <h2 className={styles.heading}>
        <strong className={styles.fontName}>{tf.font_name}</strong>
        {tf.badge_label && (
          <span className={styles.badgeLabel}> {tf.badge_label}</span>
        )}
      </h2>

      {tf.description && (
        <p className={styles.description}>{tf.description}</p>
      )}

      <CMSLink
        config={dlConfig}
        className={styles.downloadBtn}
        disabledClassName={styles.downloadBtnDisabled}
      >
        <DownloadIcon />{" "}
        {tf.button_text || (tf.force_download ? "Download Font" : "Get Font")}
      </CMSLink>

      <div className={styles.specimen}>
        {tf.alphabet_display && (
          <p
            className={styles.alphabetDisplay}
            style={{ fontWeight: WEIGHT_VALUE[active] || 400 }}
          >
            {alphabetLines(tf.alphabet_display).map((line, li) => (
              <span key={li}>{line}</span>
            ))}
          </p>
        )}

        {weights.length > 0 && (
          <div className={styles.weightsRow} role="group" aria-label="Font weight">
            {weights.map((w, wi) => (
              <button
                key={wi}
                type="button"
                className={`${styles.weight} ${
                  w.label === active ? styles.weightActive : ""
                }`}
                style={{ fontWeight: WEIGHT_VALUE[w.label] || 400 }}
                aria-pressed={w.label === active}
                onClick={() => setActive(w.label)}
              >
                {w.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function BrandTypography({ data }) {
  const { typefaces = [] } = data || {};

  if (!typefaces.length) return null;

  const bg = getValidColor(typefaces[0].background_color) || "#021526";

  return (
    <section className={styles.section} style={{ background: bg }}>
      <div className={styles.grid}>
        {typefaces.map((tf, i) => (
          <TypefaceCard key={i} tf={tf} />
        ))}
      </div>
    </section>
  );
}
